import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ImageBackground, useWindowDimensions, Alert, Platform } from "react-native";
import SvgIcon from '../components/SvgIcon';
import { useNavigation, useRoute } from '@react-navigation/native';
import colors from "../theme/colors";
import { useAuth } from "../context/AuthContext";

export default function HomeScreen() {
  const { width, height } = useWindowDimensions();
  const navigation = useNavigation();
  const route = useRoute();
  const { user, isAuthenticated, logout } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Handle PWA install prompt
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Check if already installed (running in standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      
      // Check if user dismissed banner in last 24 hours
      const dismissedTime = localStorage.getItem('installBannerDismissed');
      const dismissedRecently = dismissedTime && (Date.now() - parseInt(dismissedTime)) < 24 * 60 * 60 * 1000;
      
      if (isStandalone) {
        // App is running in standalone mode - already installed
        setShowInstallBanner(false);
        return;
      }
      
      if (dismissedRecently) {
        // User dismissed recently, don't show
        setShowInstallBanner(false);
        return;
      }
      
      // Listen for the beforeinstallprompt event
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setInstallPrompt(e);
        setShowInstallBanner(true);
        // Store the event globally so it persists
        window.deferredPrompt = e;
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      
      // Check if we have a stored prompt from before
      if (window.deferredPrompt) {
        setInstallPrompt(window.deferredPrompt);
        setShowInstallBanner(true);
      }
      
      // For Android/Chrome - always show banner if not in standalone mode
      // The install button will show instructions if no native prompt available
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // Show banner for mobile devices that aren't in standalone mode
      if ((isAndroid || isIOS) && !isStandalone && !dismissedRecently) {
        // Delay showing banner by 2 seconds to not be intrusive
        const timer = setTimeout(() => {
          setShowInstallBanner(true);
        }, 2000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        };
      }
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      try {
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowInstallBanner(false);
          localStorage.removeItem('installBannerDismissed');
        }
        setInstallPrompt(null);
        window.deferredPrompt = null;
      } catch (err) {
        console.log('Install prompt error:', err);
        showManualInstructions();
      }
    } else {
      showManualInstructions();
    }
  };

  const showManualInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.alert('Install KalyanaMitra\n\nTap the Share button (square with arrow) at the bottom of Safari, then select "Add to Home Screen".');
    } else {
      window.alert('Install KalyanaMitra\n\nTap the menu (⋮) at the top right of your browser, then select "Add to Home screen" or "Install app".');
    }
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    // Remember dismissal for 24 hours
    if (Platform.OS === 'web') {
      localStorage.setItem('installBannerDismissed', Date.now().toString());
    }
  };

  // Handle navigation parameter to open login
  useEffect(() => {
    if (route.params?.openLogin) {
      navigation.push('Login', { loginType: route.params.openLogin });
      // Clear the parameter
      navigation.setParams({ openLogin: undefined });
    }
  }, [route.params?.openLogin]);

  const openLogin = (type) => {
    navigation.push('Login', { loginType: type });
  };

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Logout', onPress: async () => await logout(), style: 'destructive' }
      ]
    );
  };

  return (
    <View style={styles.container}>
      {/* Install Banner */}
      {showInstallBanner && (
        <View style={styles.installBanner}>
          <View style={styles.installBannerContent}>
            <SvgIcon name="download" size={24} color={colors.white} />
            <View style={styles.installBannerText}>
              <Text style={styles.installBannerTitle}>Install KalyanaMitra</Text>
              <Text style={styles.installBannerSubtext}>Add to home screen for quick access</Text>
            </View>
          </View>
          <View style={styles.installBannerActions}>
            <TouchableOpacity style={styles.installButton} onPress={handleInstallClick}>
              <Text style={styles.installButtonText}>Install</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.dismissButton} onPress={dismissInstallBanner}>
              <SvgIcon name="close" size={20} color={colors.textMuted} />
            </TouchableOpacity>
          </View>
        </View>
      )}
      
      {/* Hero Section */}
      <ImageBackground 
        source={require('../assets/hero-bg.jpg')} 
        style={styles.hero}
        resizeMode="cover"
      >
        <View style={styles.heroOverlay}>
          {/* Top Title */}
          <Text style={styles.heroTitle}>Simplifying the Marriage Journey</Text>
          
          {/* Middle Content */}
          <View style={styles.middleContent}>
            <View style={styles.badgeContainer}>
              <View style={styles.badge}>
                <View style={styles.badgeIconContainer}>
                  <SvgIcon name="shield-checkmark" size={20} color={colors.secondary} />
                </View>
                <Text style={styles.badgeTitle}>Trusted</Text>
                <Text style={styles.badgeSubtext}>Mediators</Text>
              </View>
              <View style={styles.badge}>
                <View style={styles.badgeIconContainer}>
                  <SvgIcon name="heart" size={20} color={colors.secondary} />
                </View>
                <Text style={styles.badgeTitle}>Meaningful</Text>
                <Text style={styles.badgeSubtext}>Matches</Text>
              </View>
              <View style={styles.badge}>
                <View style={styles.badgeIconContainer}>
                  <SvgIcon name="sparkles" size={20} color={colors.secondary} />
                </View>
                <Text style={styles.badgeTitle}>Lasting</Text>
                <Text style={styles.badgeSubtext}>Bonds</Text>
              </View>
            </View>
            
            <Text style={styles.heroSubtitle}>
              KalyanaMitra connects matchseekers with trusted mediators, blending tradition and modern technology.
            </Text>
          </View>
          
          {/* Bottom Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.buttonPrimary} onPress={() => openLogin('mediator')}>
              <SvgIcon name="person" size={20} color={colors.white} style={styles.buttonIcon} />
              <Text style={styles.buttonText} numberOfLines={1}>Mediator Login</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.buttonSecondary} onPress={() => openLogin('matchseeker')}>
              <SvgIcon name="person" size={20} color={colors.white} style={styles.buttonIcon} />
              <Text style={styles.buttonTextSecondary} numberOfLines={1}>Match Seeker Login</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ImageBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  installBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.secondary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.accent,
  },
  installBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  installBannerText: {
    flex: 1,
  },
  installBannerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  installBannerSubtext: {
    fontSize: 12,
    color: '#E5E7EB',
  },
  installBannerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  installButton: {
    backgroundColor: colors.accent,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  installButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
  },
  dismissButton: {
    padding: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
  },
  content: {
    alignItems: "center",
    paddingBottom: 120,
  },
  hero: {
    flex: 1,
    width: "100%",
  },
  heroOverlay: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 24,
    paddingTop: 50,
    paddingBottom: 40,
    justifyContent: "space-between",
    backgroundColor: "rgba(15, 23, 42, 0.85)",
  },
  middleContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 20,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: colors.white,
    marginBottom: 16,
    lineHeight: 36,
    textAlign: "center",
  },
  badgeContainer: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 24,
    flexShrink: 0,
  },
  badge: {
    alignItems: "center",
    borderWidth: 2,
    borderColor: colors.secondary,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
    flex: 1,
    minWidth: 100,
    maxWidth: 120,
  },
  badgeIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.gray800,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
  },
  badgeTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.white,
  },
  badgeSubtext: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.white,
  },
  heroSubtitle: {
    fontSize: 16,
    color: colors.white,
    lineHeight: 26,
    marginBottom: 32,
    opacity: 0.95,
    textAlign: "center",
  },
  buttonContainer: {
    width: "100%",
    alignItems: "center",
    gap: 16,
  },
  buttonPrimary: {
    width: "60%",
    minWidth: 200,
    flexDirection: "row",
    backgroundColor: colors.secondary,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  buttonSecondary: {
    width: "60%",
    minWidth: 200,
    flexDirection: "row",
    backgroundColor: "transparent",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  buttonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    flexShrink: 0,
  },
  buttonTextSecondary: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    flexShrink: 0,
  },
});
